import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { formatTL, STATUS_LABELS } from "../../lib/api";
import { Users, ClipboardList, HardHat, TrendingUp, AlertTriangle, Trophy, Radio, Globe2, Inbox, Building2, ArrowRight, Lightbulb } from "lucide-react";
import { ChannelBadge } from "../../components/common";
import { channelColor } from "../../lib/channels";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar
} from "recharts";

const StatCard = ({ icon: Icon, label, value, color = "blue", testid }) => (
  <Card className="p-5 border border-slate-200 shadow-none card-lift" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">{label}</div>
        <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
      </div>
      <div className={`h-10 w-10 rounded-lg bg-${color}-50 text-${color}-600 flex items-center justify-center`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </Card>
);

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [web, setWeb] = useState(null);

  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setData(r.data)).finally(() => setLoading(false));
    const loadWeb = () => api.get("/analytics/overview", { params: { days: 7 } }).then((r) => setWeb(r.data)).catch(() => {});
    loadWeb();
    const t = setInterval(loadWeb, 30000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  if (!data) return null;

  const statusData = Object.entries(data.status_counts).map(([k, v]) => ({
    name: STATUS_LABELS[k]?.label || k, value: v
  }));

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Genel Bakış</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-1">
            Saha Operasyon Merkezi
          </h1>
        </div>
        <div className="text-sm text-slate-500">{new Date().toLocaleDateString("tr-TR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div>
      </div>

      {web && (() => {
        const top = web.channels.filter((c) => c.sessions > 0)[0];
        const best = [...web.channels].filter((c) => c.sessions >= 5).sort((a, b) => b.conv_rate - a.conv_rate)[0];
        const lines = [];
        lines.push(`Bugün ${web.kpi.today} tekil ziyaretçi geldi; dün ${web.kpi.yesterday}.`);
        if (top) lines.push(`Son ${web.days} günde en çok ziyaretçi ${top.label} kanalından geldi (${top.sessions} oturum).`);
        if (best && top && best.channel !== top.channel) lines.push(`Dönüşümü en yüksek kanal ${best.label} (%${best.conv_rate}).`);
        const todos = [];
        if (web.kpi.new_messages > 0) todos.push({ t: `${web.kpi.new_messages} yeni mesajı yanıtla`, to: "/admin/messages" });
        if (web.kpi.new_dealers > 0) todos.push({ t: `${web.kpi.new_dealers} bayilik başvurusunu incele`, to: "/admin/dealers" });
        if (data.open_jobs > 0) todos.push({ t: `${data.open_jobs} açık servis kaydını takip et`, to: "/admin/jobs" });
        if (data.low_stock.length > 0) todos.push({ t: `${data.low_stock.length} parçada stok azaldı`, to: "/admin/parts" });
        return (
          <Card className="p-5 border border-slate-200 shadow-none bg-gradient-to-br from-blue-50/70 to-white" data-testid="dashboard-summary">
            <div className="flex items-center gap-2 mb-3"><Lightbulb className="h-4 w-4 text-amber-500" /><h3 className="font-display text-lg font-semibold text-slate-900">Günlük Özet</h3></div>
            <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">{lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500 mr-1">Yapılacaklar</span>
              {todos.length === 0 ? <span className="text-sm text-emerald-700">Bekleyen iş yok 🎉</span> : todos.map((t) => (
                <Link key={t.to} to={t.to} className="px-3 py-1 rounded-full bg-white border border-slate-200 text-sm text-slate-700 hover:border-blue-300 hover:text-blue-700">{t.t}</Link>
              ))}
            </div>
          </Card>
        );
      })()}

      {web && (
        <Card className="p-5 border border-slate-200 shadow-none" data-testid="dashboard-web">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Globe2 className="h-4 w-4 text-blue-600" />
              <h3 className="font-display text-lg font-semibold text-slate-900">Web Sitesi — Canlı</h3>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 ml-1"><span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />CANLI</span>
            </div>
            <Link to="/admin/visitors" className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1" data-testid="dash-visitors-link">Ayrıntılı analitik <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100"><div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700">Şu an aktif</div><div className="font-display text-3xl font-bold text-emerald-700 mt-1" data-testid="dash-active">{web.kpi.active_now}</div></div>
            <div className="p-3 rounded-lg bg-slate-50"><div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Bugün (tekil)</div><div className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid="dash-today">{web.kpi.today}</div></div>
            <div className="p-3 rounded-lg bg-slate-50"><div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Bu hafta</div><div className="font-display text-3xl font-bold text-slate-900 mt-1">{web.kpi.week}</div></div>
            <Link to="/admin/messages" className="p-3 rounded-lg bg-blue-50 border border-blue-100 hover:bg-blue-100/60 transition-colors"><div className="text-[11px] uppercase tracking-wider font-bold text-blue-700 flex items-center gap-1"><Inbox className="h-3 w-3" />Yeni mesaj</div><div className="font-display text-3xl font-bold text-blue-700 mt-1" data-testid="dash-new-messages">{web.kpi.new_messages}</div></Link>
            <Link to="/admin/dealers" className="p-3 rounded-lg bg-violet-50 border border-violet-100 hover:bg-violet-100/60 transition-colors"><div className="text-[11px] uppercase tracking-wider font-bold text-violet-700 flex items-center gap-1"><Building2 className="h-3 w-3" />Yeni bayilik başvurusu</div><div className="font-display text-3xl font-bold text-violet-700 mt-1" data-testid="dash-new-dealers">{web.kpi.new_dealers}</div></Link>
          </div>
          {web.channels.filter((c) => c.sessions > 0).length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Son 7 gün — ziyaretçi kanalları</div>
              <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
                {web.channels.filter((c) => c.sessions > 0).map((c) => (
                  <div key={c.channel} title={`${c.label}: ${c.sessions}`} style={{ width: `${(c.sessions * 100) / web.kpi.sessions}%`, background: channelColor(c.channel) }} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
                {web.channels.filter((c) => c.sessions > 0).slice(0, 6).map((c) => (
                  <div key={c.channel} className="flex items-center gap-2 text-sm"><ChannelBadge channel={c.channel} /><span className="font-mono font-semibold text-slate-800">{c.sessions}</span><span className="text-xs text-slate-400">%{Math.round((c.sessions * 100) / web.kpi.sessions)}</span></div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ClipboardList} label="Açık Servis" value={data.open_jobs} color="blue" testid="stat-open" />
        <StatCard icon={Users} label="Müşteri" value={data.total_customers} color="indigo" testid="stat-customers" />
        <StatCard icon={HardHat} label="Aktif Teknisyen" value={data.total_technicians} color="amber" testid="stat-technicians" />
        <StatCard icon={TrendingUp} label="Toplam Gelir" value={formatTL(data.total_revenue)} color="emerald" testid="stat-revenue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 border border-slate-200 shadow-none lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-900">Son 7 Gün — Ciro</h3>
            <div className="text-xs text-slate-500">Tamamlanan servislerden</div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.daily_revenue}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#94A3B8" fontSize={12} />
                <YAxis stroke="#94A3B8" fontSize={12} />
                <Tooltip formatter={(v) => formatTL(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Area type="monotone" dataKey="amount" stroke="#2563EB" strokeWidth={2.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border border-slate-200 shadow-none">
          <h3 className="font-display text-lg font-semibold text-slate-900 mb-4">Durum Dağılımı</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusData} layout="vertical" margin={{ left: 30 }}>
                <XAxis type="number" stroke="#94A3B8" fontSize={11} />
                <YAxis dataKey="name" type="category" stroke="#475569" fontSize={11} width={80} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="value" fill="#F97316" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border border-slate-200 shadow-none">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="h-4 w-4 text-amber-500" />
            <h3 className="font-display text-lg font-semibold text-slate-900">En Çok Tamamlayan Teknisyenler</h3>
          </div>
          {data.top_technicians.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Henüz veri yok</div>
          ) : (
            <div className="space-y-2">
              {data.top_technicians.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-md bg-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="h-7 w-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                    <span className="font-medium text-slate-900">{t.name}</span>
                  </div>
                  <span className="font-mono text-sm font-bold text-slate-900">{t.completed}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-6 border border-slate-200 shadow-none">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <h3 className="font-display text-lg font-semibold text-slate-900">Stok Uyarısı</h3>
          </div>
          {data.low_stock.length === 0 ? (
            <div className="text-sm text-slate-500 py-8 text-center">Stok seviyeleri normal</div>
          ) : (
            <div className="space-y-2">
              {data.low_stock.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-md border border-amber-100 bg-amber-50/50">
                  <div>
                    <div className="font-medium text-slate-900 text-sm">{p.name}</div>
                    <div className="text-xs text-slate-500">SKU: {p.sku || "-"}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-bold text-amber-700">{p.stock_qty} {p.unit}</div>
                    <div className="text-[10px] text-slate-500">min: {p.min_stock}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
